import { useBoolean } from '@csp/fe-hooks';
import { useCallback, useEffect } from 'react';
import { CallbackAsync, CallbackAsyncWith } from '@csp/csp-common-model';
import { OnClick } from '@csp/csp-web-ui';

type OpenDrawer = OnClick;
type CloseDrawer = CallbackAsync;
type DrawerMounted = boolean;
type DrawerOpen = boolean;

type DrawerHook = [OpenDrawer, CloseDrawer, DrawerMounted, DrawerOpen];

export const useDrawerMount = (initiallyOpenAndMounted = false, timeoutOverride?: number): DrawerHook => {
  const [isOpen, setOpen, setClosed] = useBoolean(initiallyOpenAndMounted);
  const [isMounted, setMounted, setUnmounted] = useBoolean(initiallyOpenAndMounted);

  useEffect(() => {
    /**
     * open/close animation does not work if drawer is opened on the same render as
     * it is mounted (i.e. it cannot be mounted in opened state for animation to work).
     * useEffect to split the mounting and opening into 2 renders.
     **/
    if (isMounted) {
      setOpen();
    }
  }, [isMounted, setOpen]);

  const openDrawer = useCallback(() => {
    setMounted();
  }, [setMounted]);

  const closeDrawer = useCallback(async () => {
    const delay: CallbackAsyncWith<number> = async timeout => new Promise(resolve => setTimeout(resolve, timeout));
    /**
     * The setUnmounted() happens with some delay due to allow the drawer's slider animation to finish before
     * unmounting.
     */
    const unmount: CallbackAsync = async () => {
      await delay(timeoutOverride ?? 500);
      setUnmounted();
    };

    setClosed();
    await unmount();
  }, [setClosed, setUnmounted, timeoutOverride]);

  return [openDrawer, closeDrawer, isMounted, isOpen];
};
